import { useEffect, useRef } from 'react';
import { useBeforeUnload, useBlocker, useLocation, type Location } from 'react-router-dom';

export const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Leave this page and discard them?';

const locationPath = (targetLocation: Location): string =>
  `${targetLocation.pathname}${targetLocation.search}${targetLocation.hash}`;

/** Guards tool drafts and returns a one-shot allowance for successful-create navigation. */
export const useUnsavedChangesGuard = (hasUnsavedChanges: boolean) => {
  const allowedLocationRef = useRef<string | null>(null);
  const renderedLocation = useLocation();
  const blocker = useBlocker(
    ({
      currentLocation: routeLocation,
      nextLocation,
    }: {
      currentLocation: Location;
      nextLocation: Location;
    }) => {
      const nextLocationPath = locationPath(nextLocation);
      const locationWillChange = locationPath(routeLocation) !== nextLocationPath;
      const nextNavigationIsAllowed = allowedLocationRef.current === nextLocationPath;
      return hasUnsavedChanges && locationWillChange && !nextNavigationIsAllowed;
    },
  );

  // Consume the one-shot allowance only after React Router commits the destination.
  useEffect(() => {
    if (allowedLocationRef.current === locationPath(renderedLocation)) {
      allowedLocationRef.current = null;
    }
  }, [renderedLocation]);

  // Blocked transitions require an imperative decision to resume or reset the router.
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm(UNSAVED_CHANGES_MESSAGE)) blocker.proceed();
    else blocker.reset();
  }, [blocker]);

  useBeforeUnload(event => {
    if (!hasUnsavedChanges) return;
    event.preventDefault();
    event.returnValue = UNSAVED_CHANGES_MESSAGE;
  });

  return (allowedPath: string) => {
    allowedLocationRef.current = allowedPath;
  };
};
